import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError, apiErrorMessage } from '@/lib/api/client';
import { useFetchLlmCostJobLogUrl } from '@/lib/query/hooks/useDashboardLlmCost';
import type { LlmCostJob } from '@/lib/types/dashboard/llmCost';
import { formatDateTime, formatDurationMs, formatTab, formatTokens, formatUsd } from './format';

function statusChip(job: LlmCostJob) {
  if (job.status === 'succeeded') {
    return <Chip size="small" label="成功" color="success" variant="outlined" />;
  }
  return (
    <Tooltip title={job.errorMessage ?? ''}>
      <Chip
        size="small"
        label={job.errorCode ? `失敗: ${job.errorCode}` : '失敗'}
        color="error"
        variant="outlined"
      />
    </Tooltip>
  );
}

function costCell(job: LlmCostJob) {
  if (job.costUnknown) {
    return (
      <Tooltip title="Agent の結果を受け取れずに終わったため、コストを観測できていません">
        <Chip size="small" label="不明" color="warning" variant="outlined" />
      </Tooltip>
    );
  }
  return formatUsd(job.costUsd);
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Box>
  );
}

function JobDetail({ job }: { job: LlmCostJob }) {
  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'grid', gap: 2 }}>
      <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={2}>
        <DetailField label="jobId" value={job.jobId} />
        <DetailField
          label="結果 revision"
          value={job.resultRevision === null ? '—' : `r${job.resultRevision}`}
        />
        <DetailField label="自動公開" value={job.activation} />
        <DetailField
          label="開始 / 終了"
          value={`${formatDateTime(job.createdAt)} / ${formatDateTime(job.finishedAt)}`}
        />
      </Box>
      {job.errorMessage && <DetailField label="error message" value={job.errorMessage} />}
      {job.summary && <DetailField label="summary" value={job.summary} />}
      {job.agentNote && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            agentNote（最終応答末尾）
          </Typography>
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{job.agentNote}</pre>
        </Box>
      )}
    </Box>
  );
}

/** Agent のジョブログ（S3 の JSON Lines）を presigned URL 経由でダウンロードするボタン（T-0043） */
function JobLogButton({ job }: { job: LlmCostJob }) {
  const fetchUrl = useFetchLlmCostJobLogUrl();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const url = await fetchUrl(job.jobId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        toast.info(
          'このジョブのログはありません（preflight 失敗・ログ機能導入前のジョブ・14 日経過のいずれか）',
        );
      } else {
        toast.error(apiErrorMessage(error, 'ジョブログの取得に失敗しました'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Agent のジョブログ（JSON Lines）をダウンロード">
      <span>
        <IconButton
          size="small"
          aria-label="ジョブログをダウンロード"
          disabled={loading}
          onClick={() => void handleClick()}
        >
          <DownloadIcon size={16} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

function JobRow({ job }: { job: LlmCostJob }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label="詳細" onClick={() => setOpen((v) => !v)}>
            {open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDateTime(job.createdAt)}</TableCell>
        <TableCell>
          {job.accountCode} / {formatTab(job.tabId, job.tabName)}
          {job.trigger === 'batch' && (
            <Chip size="small" label="定期" variant="outlined" sx={{ ml: 1 }} />
          )}
        </TableCell>
        <TableCell>{statusChip(job)}</TableCell>
        <TableCell align="right">{costCell(job)}</TableCell>
        <TableCell align="right">
          {formatTokens(job.inputTokens)} / {formatTokens(job.outputTokens)}
        </TableCell>
        <TableCell align="right">{formatDurationMs(job.durationMs)}</TableCell>
        <TableCell align="center" padding="checkbox">
          <JobLogButton job={job} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderBottom: open ? undefined : 'unset' }}>
          <Collapse in={open} unmountOnExit>
            <JobDetail job={job} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/** 終端済みジョブ一覧（行展開で結果 revision・エラー・summary・agentNote） */
export function LlmCostJobsTable({ jobs }: { jobs: LlmCostJob[] }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" mb={1}>
        ジョブ一覧
        <Typography component="span" variant="caption" color="text.secondary" ml={1}>
          新しい順・最大 500 件
        </Typography>
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>日時</TableCell>
              <TableCell>アカウント / タブ</TableCell>
              <TableCell>結果</TableCell>
              <TableCell align="right">コスト</TableCell>
              <TableCell align="right">トークン 入 / 出</TableCell>
              <TableCell align="right">所要時間</TableCell>
              <TableCell align="center">ログ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <JobRow key={job.jobId} job={job} />
            ))}
            {jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  データがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
